import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardState } from "components/DashboardContext";
import { findFoldersTreeObj, getFoldersBreadcrumbs } from "utils/helpers";
import { CSSTransition } from "react-transition-group";
import "./FolderExplorer.scss";

import Grid from 'components/uiComponents/Grid';
import UniversalLoader from "components/uiComponents/UniversalLoader";
import BreabcrumbsItem from 'components/uiComponents/BreabcrumbsItem';
import SortHeader from 'components/uiComponents/SortHeader';

import { ReactComponent as GridIcon } from "static/icons/grid.svg";
import { ReactComponent as ListIcon } from "static/icons/list.svg";
import { ReactComponent as EmptyFolderIcon } from "static/images/empty-folder.svg";

const FolderExplorer = memo(() => {
    const { t } = useTranslation();

    const {
        foldersTree,
        activeFolder,
        isFolderLoading,
        favourites,
        recent,
        setActiveFolder,
    } = useDashboardState();

    const standardSortList = [
        {
            key: 'name',
            value: t('name'),
            isDescending: false,
            isActive: true,
        },
        {
            key: 'version',
            value: t('version'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'description',
            value: t('description_model'),
            isDescending: false,
            isActive: false,
        },
        {
            key: 'size',
            value: t('size'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'lastUpd',
            value: t('last_update'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'updBy',
            value: t('updated_by'),
            isDescending: true,
            isActive: false,
        },
    ];

    const recentSortList = [
        {
            key: 'name',
            value: t('name'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'version',
            value: t('version'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'description',
            value: t('description_model'),
            isDescending: false,
            isActive: false,
        },
        {
            key: 'size',
            value: t('size'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'lastUpd',
            value: t('last_update'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'updBy',
            value: t('updated_by'),
            isDescending: true,
            isActive: false,
        },
        {
            key: 'lastOpened',
            value: t('last_opend'),
            isDescending: true,
            isActive: true,
        },
    ];

    const [itemsViewType, setItemsViewType] = useState('list');
    const [titleWidth, setTitleWidth] = useState(260);
    const [sortData, setSortData] = useState(standardSortList);

    useEffect(() => {
        if (activeFolder === 'recent') {
            setSortData(recentSortList);
        } else {
            setSortData(standardSortList);
        }
    }, [activeFolder]);

    const handleViewChange = () => {
        if (itemsViewType === 'list') {
            setItemsViewType('block');
        } else {
            setItemsViewType('list');
        }
    };

    const handleChooseSortType = useCallback((item) => {
        setSortData(prevState => {
            prevState.forEach(el => {
                if (el.key === item.key) {
                    el.isActive = true;
                    el.isDescending = true;
                } else {
                    el.isActive = false;
                    el.isDescending = true;
                }
            });

            return prevState;
        });
    }, []);

    const handleDirectionButtonClick = () => {
        const sortDataCopy = JSON.parse(JSON.stringify(sortData));
        const activeItem = sortDataCopy.find(el => el.isActive);

        if (activeItem.isDescending) {
            activeItem.isDescending = false;
        } else {
            activeItem.isDescending = true;
        }

        setSortData(sortDataCopy);
    };

    const handleSortItemClick = item => {
        const sortDataCopy = JSON.parse(JSON.stringify(sortData));

        sortDataCopy.forEach(el => {
            if (el.key === item.key) {
                el.isActive = true;

                if (el.isDescending) {
                    el.isDescending = false;
                } else {
                    el.isDescending = true;
                }
            } else {
                el.isActive = false;
                el.isDescending = true;
            }
        });

        setSortData(sortDataCopy);
    };

    const handleWidthChange = width => {
        if (width) {
            setTitleWidth(width);
        }
    };

    const folderObj = useMemo(() => {
        let obj = null

        if (!isFolderLoading) {
            if (activeFolder === 'favourites') {
                obj = favourites;
            } else if (activeFolder === 'recent') {
                obj = recent;
            } else if (activeFolder) {
                obj = findFoldersTreeObj(activeFolder, foldersTree);
            }
        }

        const sortItem = sortData.find(item => item.isActive);

        if (obj) {
            switch (sortItem.key) {
                case 'name':
                    obj?.childrenArr?.sort((a, b) => {
                        if (sortItem.isDescending) {
                            return b?.text?.localeCompare(a?.text);
                        } else {
                            return a?.text?.localeCompare(b?.text);
                        }
                    });
                    break;
                case 'version':
                    obj?.childrenArr?.sort((a, b) => {
                        const getVersionNumber = (item) => {
                            const match = String(item?.lastVersion).match(/\d+/); 
                            return match ? parseInt(match[0], 10) : 0;
                        };
                    
                        const aVer = getVersionNumber(a);
                        const bVer = getVersionNumber(b);
                    
                        if (sortItem.isDescending) {
                            return bVer - aVer;
                        } else {
                            return aVer - bVer;
                        }
                    });
                    break;
                case 'size':
                    obj?.childrenArr?.sort((a, b) => {
                        if (sortItem.isDescending) {
                            return Number(b?.size) - Number(a?.size);
                        } else {
                            return Number(a?.size) - Number(b?.size);
                        }
                    });
                    break;
                case 'lastUpd':
                    obj?.childrenArr?.sort((a, b) => {
                        if (sortItem.isDescending) {
                            return Number(b?.lastUpdate) - Number(a?.lastUpdate);
                        } else {
                            return Number(a?.lastUpdate) - Number(b?.lastUpdate);
                        }
                    })
                    break;
                case 'lastOpened':
                    obj?.childrenArr?.sort((a, b) => {
                        if (sortItem.isDescending) {
                            return Number(b?.lastOpened) - Number(a?.lastOpened);
                        } else {
                            return Number(a?.lastOpened) - Number(b?.lastOpened);
                        }
                    })
                    break;
                case 'updBy':
                    obj.childrenArr?.sort((a, b) => {
                        if (sortItem.isDescending) {
                            return b?.updatedBy?.localeCompare(a?.updatedBy);
                        } else {
                            return a?.updatedBy?.localeCompare(b?.updatedBy);
                        }
                    });
                    break;
            }
        }

        return obj;
    }, [foldersTree, activeFolder, isFolderLoading, favourites, recent, sortData]);
 
    const breadcrumbsArr = useMemo(() => {
        if (!isFolderLoading) {
            if (activeFolder !== 'favourites' && activeFolder !== 'recent' && activeFolder) {
                const breadcrumbs = getFoldersBreadcrumbs(activeFolder, foldersTree);
                breadcrumbs?.shift();

                return breadcrumbs;
            }

            if (activeFolder === 'favourites') {
                return [{
                    id: 'favourites',
                    text: t('favorites')
                }];
            }

            if (activeFolder === 'recent') {
                return [{
                    id: 'recent',
                    text: t('recent')
                }];
            }
        }
    }, [foldersTree, activeFolder, isFolderLoading]);

    return (
        <>
            <div className="folder-explorer__header">
                <CSSTransition
                    in={breadcrumbsArr?.length > 0}
                    timeout={150}
                    unmountOnExit
                    classNames="animation"
                >
                    <>
                        <div className="folder-explorer__breadcrumbs" >
                            {breadcrumbsArr?.map((item, index) => {
                                const handleBreadcrumbsItemClick = () => {
                                    setActiveFolder(item.id)
                                };

                                return (
                                    <BreabcrumbsItem
                                        key={item.id}
                                        text={item.text}
                                        isFirst={index === 0}
                                        isLast={index + 1 === breadcrumbsArr.length}
                                        onClick={handleBreadcrumbsItemClick}
                                    />
                                );
                            })}
                        </div>

                        {folderObj?.childrenArr?.length > 0 &&
                            <div className="folder-explorer__header_btns">
                                <CSSTransition
                                    in={itemsViewType === 'list'}
                                    timeout={150}
                                    unmountOnExit
                                    classNames="animation"
                                >
                                    <GridIcon
                                        className="folder-explorer__header_btn"
                                        onClick={handleViewChange}
                                    />
                                </CSSTransition>

                                <CSSTransition
                                    in={itemsViewType === 'block'}
                                    timeout={150}
                                    unmountOnExit
                                    classNames="animation"
                                >
                                    <ListIcon
                                        className="folder-explorer__header_btn"
                                        onClick={handleViewChange}
                                    />
                                </CSSTransition>
                            </div>
                        }
                    </>
                </CSSTransition>
            </div>

            <div className="folder-explorer__grid">
                {folderObj &&
                    Array.isArray(folderObj.childrenArr) &&
                    (breadcrumbsArr?.length > 0 || activeFolder === 'favourites' || activeFolder === 'recent') &&
                    <CSSTransition
                        in={breadcrumbsArr?.length > 0 || activeFolder === 'favourites' || activeFolder === 'recent'}
                        timeout={150}
                        unmountOnExit
                        classNames="animation"
                    >
                        <>
                            {folderObj.childrenArr.length > 0 &&
                                <SortHeader
                                    data={sortData}
                                    viewType={itemsViewType}
                                    onChooseSortType={handleChooseSortType}
                                    onDirectionButtonClick={handleDirectionButtonClick}
                                    onSortItemClick={handleSortItemClick}
                                    onWidthChange={handleWidthChange}
                                />
                            }
                            {
                                !isFolderLoading && !folderObj.childrenArr.length ? (
                                    <div className="empty-folder">
                                        <div className="empty-folder__container">
                                            <EmptyFolderIcon className={'empty-folder_img'} />
                                            <h3 className={"empty-folder__text"}>{folderObj?.text}&nbsp;{t('files_not_found')}</h3>
                                            <p className={"empty-folder__text"}>
                                                {t('files_recently_viewed')}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="folder-explorer__grid_wrapper">
                                        <Grid
                                            itemsViewType={itemsViewType}
                                            items={folderObj?.childrenArr}
                                            titleWidth={titleWidth}
                                        />
                                    </div>
                                )
                            }
                        </>
                    </CSSTransition>
                }

                <CSSTransition
                    in={isFolderLoading}
                    timeout={50}
                    unmountOnExit
                    classNames="animation"
                >
                    <UniversalLoader />
                </CSSTransition>
            </div>
        </>
    );
});

export default FolderExplorer;
